import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import {
  ACCESS_TOKEN_HEADER,
  NON_AUTHENTICATION_ENDPOINTS,
  LOCAL_STORAGE_ACCESS_TOKEN_KEY,
  LOCAL_STORAGE_REFRESH_TOKEN_KEY,
  LOCAL_STORAGE_USER_KEY,
} from './auth';
import { API_SERVER } from '@config/environment';
import store from '@store/store';
import { actions as userActions } from '@store/slice/user-slice';
import { ESERVER_ERROR_CODE } from '@datatype/enum';
import { SERVER_CONNECTION_FAIL } from '@constant/error';

// ETag 기반 HTTP 캐싱: 서버 응답이 변경되지 않으면 304 응답으로 네트워크 대역폭 절약
// 주의: 메모리 캐시이므로 새로고침 시 초기화됨
// ###################################
// etag cache
// - etag를 비교하여, etag가 같으면 브라우저캐시(304응답) 다르면 서버요청(200응답)
// - 새로고침하면 etag 사라짐 (캐시 X)
// ###################################
const etagCache: Record<string, { etag: string; data: any }> = {};

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let reissueAccessToken: Promise<AxiosResponse> | null = null;

const instance = axios.create({
  baseURL: API_SERVER,
  // status 400 & 500 -> "useMoashelfApiQuery & useApiMutation"의 onError 실행 (useMoashelfApiQuery & useApiMutation 보다 먼저 실행)
  validateStatus: (status) => {
    return (status >= 200 && status < 300) || (status >= 300 && status < 400);
  },
  withCredentials: true,
  headers: {
    'Cache-Control': 'no-cache',
    ...(process.env.NODE_ENV === 'development' && { 'ngrok-skip-browser-warning': 'true' }),
  },
});

instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // await new Promise((resolve) => setTimeout(resolve, 10000));

    const isNonAuthenticationApi = NON_AUTHENTICATION_ENDPOINTS.some((endpoint) =>
      (config.url ?? '').startsWith(endpoint),
    );
    const accessToken = localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN_KEY);

    if (!isNonAuthenticationApi && accessToken) {
      config.headers[ACCESS_TOKEN_HEADER] = `Bearer ${accessToken}`;
    }

    // ETag 비교 (If-None-Match)
    if (config.method?.toLowerCase() === 'get' && config.url) {
      const endpoint = instance.getUri(config);
      // endpoint: http://localhost:8080/api/v1/ig/student/me
      // base url: http://localhost:8080
      // url: /api/v1/ig/student/me
      // etag: 0459f1a035398a1231f20979898e9dc09
      const cached = etagCache[endpoint];

      if (cached?.etag) config.headers['If-None-Match'] = cached.etag;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  async (response: AxiosResponse) => {
    const method = response.config.method?.toLowerCase();
    const endpoint = instance.getUri(response.config);
    // endpoint: http://localhost:8080/api/v1/ig/student/me
    // base url: http://localhost:8080
    // url: /api/v1/ig/student/me

    // GET(200) 응답 -> ETag 저장
    if (method === 'get' && response.status === 200) {
      const etag = response.headers['etag'];
      if (etag) {
        etagCache[endpoint] = { etag, data: response.data };
      }
    }

    // GET(304:Not Modified) 응답 -> 캐시된 데이터 반환
    if (method === 'get' && response.status === 304) {
      const cached = etagCache[endpoint];
      if (cached) {
        return { ...response, status: 200, data: cached.data };
      }
    }

    return response;
  },
  async (error) => {
    // ############### state ###############
    // - reject -> useApi onError로 전달
    // - error sample
    // {
    //   code:...,
    //   message:...,
    //   // server api response
    //   response:{
    //     data:{
    //       code:...,
    //       message:...
    //       status:...
    //     }
    //   }
    // }

    if (error.code === 'ERR_NETWORK') {
      console.error(`=== ${SERVER_CONNECTION_FAIL} ===`);
      return Promise.reject(SERVER_CONNECTION_FAIL);
    }

    const code = error.response?.data?.code;
    const userKey = localStorage.getItem(LOCAL_STORAGE_USER_KEY);
    const refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
    const isAuthenticated = store.getState().rootReducer.userReducer.isAuthenticated;

    if (isAuthenticated && (!code || !userKey || !refreshToken)) {
      store.dispatch(userActions.unAuthenticate());
      return Promise.reject(error);
    }

    if (code === ESERVER_ERROR_CODE.AUTH_0033) {
      const originalRequest = error.config as RetryableRequestConfig | undefined;

      if (!userKey || !refreshToken || !originalRequest || originalRequest._retry) {
        store.dispatch(userActions.unAuthenticate());
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (!reissueAccessToken) {
        // 동시에 만료된 요청은 이 Promise 하나를 기다린 뒤, 각각 원 요청을 재시도한다.
        // reissue-token 요청은 interceptor를 거치지 않도록 직접 axios 사용
        reissueAccessToken = axios
          .post(
            `${API_SERVER}/api/v1/auth/reissue-token`,
            { userKey, refreshToken },
            {
              validateStatus: (status) =>
                (status >= 200 && status < 300) ||
                (status >= 300 && status < 400) ||
                (status >= 400 && status < 500),
              withCredentials: true,
            },
          )
          .finally(() => {
            reissueAccessToken = null;
          });
      }

      const response = await reissueAccessToken;

      if (response?.data?.status === 'success') {
        const accessToken = response?.data?.data?.accessToken;
        if (!accessToken) {
          store.dispatch(userActions.unAuthenticate());
          return Promise.reject(error);
        }
        store.dispatch(userActions.authenticate({ userKey, accessToken, refreshToken }));
        // 새 access token으로 실패했던 원 요청을 한 번 재시도한다.
        return instance(originalRequest);
      } else {
        store.dispatch(userActions.unAuthenticate());
        return Promise.reject(error);
      }
    }

    if (
      [
        ESERVER_ERROR_CODE.AUTH_0031,
        ESERVER_ERROR_CODE.AUTH_0032,
        ESERVER_ERROR_CODE.AUTH_0034,
        ESERVER_ERROR_CODE.AUTH_0035,
        ESERVER_ERROR_CODE.AUTH_0036,
      ].includes(code)
    ) {
      store.dispatch(userActions.unAuthenticate());
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default instance;
